import React from 'react';
import { X } from 'lucide-react';

const AddClientModal = ({
  showAddForm,
  newClient,
  handleInputChange,
  validationErrors,
  touchedFields,
  setTouchedFields,
  addClientLoading,
  addClientError,
  handleAddClient,
  handleCloseAddForm
}) => {
  if (!showAddForm) return null;

  // Helper function to validate names (letters only, no numbers, no special characters except spaces)
  const validateName = (value, fieldName) => {
    if (!value.trim()) return `${fieldName} is required`;
    if (value.length < 2) return `${fieldName} must be at least 2 characters`;
    if (!/^[A-Za-z\s]+$/.test(value)) return `${fieldName} can only contain letters and spaces`;
    if (/^\d+$/.test(value)) return `${fieldName} cannot be only numbers`;
    return '';
  };

  // Helper function to validate city/country (letters and spaces only)
  const validateLocation = (value, fieldName) => {
    if (!value.trim()) return `${fieldName} is required`;
    if (value.length < 2) return `${fieldName} must be at least 2 characters`;
    if (!/^[A-Za-z\s]+$/.test(value)) return `${fieldName} can only contain letters and spaces`;
    return '';
  };

  // Helper function to validate address
  const validateAddress = (value, fieldName) => {
    if (!value.trim()) return `${fieldName} is required`;
    if (value.length < 5) return `${fieldName} must be at least 5 characters`;
    if (/^[0-9\s]+$/.test(value)) return `${fieldName} cannot be only numbers`;
    return '';
  };

  // Helper function to validate phone number
  const validatePhone = (value) => {
    if (!value.trim()) return 'Contact number is required';
    if (!/^\d{10}$/.test(value)) return 'Contact number must be exactly 10 digits';
    return '';
  };

  // Helper function to validate email
  const validateEmail = (value) => {
    if (!value.trim()) return 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Please enter a valid email address';
    return '';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-bold text-gray-800">Add New Client</h2>
          <button 
            onClick={handleCloseAddForm}
            disabled={addClientLoading}
            className="disabled:opacity-50 text-gray-500 hover:text-gray-700"
          >
            <X size={24} />
          </button>
        </div>
        <form onSubmit={handleAddClient} className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Client Name *</label>
              <input
                type="text"
                name="client_name"
                value={newClient.client_name}
                onChange={(e) => {
                  // Allow only letters and spaces
                  const value = e.target.value;
                  if (/^[A-Za-z\s]*$/.test(value)) {
                    handleInputChange(e);
                  }
                }}
                onBlur={(e) => setTouchedFields(prev => ({ ...prev, [e.target.name]: true }))}
                required
                disabled={addClientLoading}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 ${
                  validationErrors.client_name && touchedFields.client_name ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter client name (e.g., John Smith)"
                minLength="2"
                pattern="[A-Za-z\s]+"
                title="Client name can only contain letters and spaces"
              />
              {validationErrors.client_name && touchedFields.client_name && (
                <p className="text-red-500 text-xs mt-1">{validationErrors.client_name}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Contact Person *</label>
              <input
                type="text"
                name="contact_person"
                value={newClient.contact_person}
                onChange={(e) => {
                  // Allow only letters and spaces
                  const value = e.target.value;
                  if (/^[A-Za-z\s]*$/.test(value)) {
                    handleInputChange(e);
                  }
                }}
                onBlur={(e) => setTouchedFields(prev => ({ ...prev, [e.target.name]: true }))}
                required
                disabled={addClientLoading}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 ${
                  validationErrors.contact_person && touchedFields.contact_person ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter contact person name"
                minLength="2"
                pattern="[A-Za-z\s]+"
                title="Contact person name can only contain letters and spaces"
              />
              {validationErrors.contact_person && touchedFields.contact_person && (
                <p className="text-red-500 text-xs mt-1">{validationErrors.contact_person}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Contact Number *</label>
              <input
                type="tel"
                name="contact_phone"
                value={newClient.contact_phone}
                onChange={(e) => {
                  // Allow only numbers
                  const value = e.target.value;
                  if (/^\d*$/.test(value) && value.length <= 10) {
                    handleInputChange(e);
                  }
                }}
                onBlur={(e) => setTouchedFields(prev => ({ ...prev, [e.target.name]: true }))}
                required
                disabled={addClientLoading}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 ${
                  validationErrors.contact_phone && touchedFields.contact_phone ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter 10-digit contact number"
                maxLength="10"
                pattern="\d{10}"
                title="Please enter exactly 10 digits"
              />
              {validationErrors.contact_phone && touchedFields.contact_phone && (
                <p className="text-red-500 text-xs mt-1">{validationErrors.contact_phone}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Contact Email *</label>
              <input
                type="email"
                name="contact_email"
                value={newClient.contact_email}
                onChange={handleInputChange}
                onBlur={(e) => setTouchedFields(prev => ({ ...prev, [e.target.name]: true }))}
                required
                disabled={addClientLoading}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 ${
                  validationErrors.contact_email && touchedFields.contact_email ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter contact email"
                title="Please enter a valid email address"
              />
              {validationErrors.contact_email && touchedFields.contact_email && (
                <p className="text-red-500 text-xs mt-1">{validationErrors.contact_email}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Address Line 1 *</label>
              <input
                type="text"
                name="address_line1"
                value={newClient.address_line1}
                onChange={handleInputChange}
                onBlur={(e) => setTouchedFields(prev => ({ ...prev, [e.target.name]: true }))}
                required
                disabled={addClientLoading}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 ${
                  validationErrors.address_line1 && touchedFields.address_line1 ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter address line 1 (e.g., 123 Main Street)"
                minLength="5"
                title="Address should include street name and number"
              />
              {validationErrors.address_line1 && touchedFields.address_line1 && (
                <p className="text-red-500 text-xs mt-1">{validationErrors.address_line1}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Address Line 2</label>
              <input
                type="text"
                name="address_line2"
                value={newClient.address_line2}
                onChange={handleInputChange}
                disabled={addClientLoading}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
                placeholder="Enter address line 2 (e.g., Suite 100, Apt 2B)"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">City *</label>
              <input
                type="text"
                name="city"
                value={newClient.city}
                onChange={(e) => {
                  // Allow only letters and spaces for city
                  const value = e.target.value;
                  if (/^[A-Za-z\s]*$/.test(value)) {
                    handleInputChange(e);
                  }
                }}
                onBlur={(e) => setTouchedFields(prev => ({ ...prev, [e.target.name]: true }))}
                required
                disabled={addClientLoading}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 ${
                  validationErrors.city && touchedFields.city ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter city"
                minLength="2"
                pattern="[A-Za-z\s]+"
                title="City name can only contain letters and spaces"
              />
              {validationErrors.city && touchedFields.city && (
                <p className="text-red-500 text-xs mt-1">{validationErrors.city}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Country *</label>
              <input
                type="text"
                name="country"
                value={newClient.country}
                onChange={(e) => {
                  // Allow only letters and spaces for country
                  const value = e.target.value;
                  if (/^[A-Za-z\s]*$/.test(value)) {
                    handleInputChange(e);
                  }
                }}
                onBlur={(e) => setTouchedFields(prev => ({ ...prev, [e.target.name]: true }))}
                required
                disabled={addClientLoading}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 ${
                  validationErrors.country && touchedFields.country ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter country"
                minLength="2"
                pattern="[A-Za-z\s]+"
                title="Country name can only contain letters and spaces"
              />
              {validationErrors.country && touchedFields.country && (
                <p className="text-red-500 text-xs mt-1">{validationErrors.country}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                name="status"
                value={newClient.status}
                onChange={handleInputChange}
                disabled={addClientLoading}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          </div>

          {addClientError && (
            <div className="p-2 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
              {addClientError}
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleCloseAddForm}
              disabled={addClientLoading}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={addClientLoading || Object.keys(validationErrors).some(key => validationErrors[key])}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-blue-300 disabled:cursor-not-allowed font-medium"
            >
              {addClientLoading ? 'Adding...' : 'Add Client'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddClientModal;